import { useState, useRef } from "react";
import "./css/adminnoticeadd.css";
import { apiPostFormData } from "../../../utils/apiClient";

const AdminNoticeAdd = ({ onClose, onAdd }) => {
  const [title, setTitle] = useState("");
  const [isPinned, setIsPinned] = useState(false);
  const [noticeType, setNoticeType] = useState("공통");
  const [loading, setLoading] = useState(false);
  const [descImgFiles, setDescImgFiles] = useState([]);
  const [attachFiles, setAttachFiles] = useState([]);

  const descEditorRef = useRef(null);
  const descImgInputRef = useRef(null);
  const savedRangeRef = useRef(null);

  const handleDescImageInsert = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      alert("이미지 파일 크기는 10MB를 초과할 수 없습니다.");
      return;
    }

    const blobUrl = URL.createObjectURL(file);
    const img = document.createElement("img");
    img.src = blobUrl;
    img.style.maxWidth = "100%";
    img.style.height = "auto";

    const range =
      savedRangeRef.current ||
      (() => {
        const r = document.createRange();
        r.selectNodeContents(descEditorRef.current);
        r.collapse(false);
        return r;
      })();
    savedRangeRef.current = null;

    descEditorRef.current.focus();
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
    range.deleteContents();
    range.insertNode(img);
    range.setStartAfter(img);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);

    setDescImgFiles((prev) => [...prev, { blobUrl, file }]);
  };

  const handleAttachFileChange = (e) => {
    const files = Array.from(e.target.files);
    e.target.value = "";
    if (files.length === 0) return;

    const maxSize = 10 * 1024 * 1024;
    const oversizedFiles = files.filter((file) => file.size > maxSize);
    if (oversizedFiles.length > 0) {
      alert("첨부파일 크기는 10MB를 초과할 수 없습니다.");
      return;
    }

    setAttachFiles((prev) => [...prev, ...files]);
  };

  const handleAttachFileRemove = (idx) => {
    setAttachFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      alert("제목을 입력해주세요.");
      return;
    }

    const contentHtml = descEditorRef.current?.innerHTML || "";
    if (!contentHtml.trim() || contentHtml === "<br>") {
      alert("내용을 입력해주세요.");
      return;
    }

    setLoading(true);

    try {
      const formDataObj = new FormData();
      formDataObj.append("title", title);
      formDataObj.append("isPinned", isPinned);
      formDataObj.append("noticeType", noticeType);

      let processedHtml = contentHtml;
      descImgFiles.forEach(({ blobUrl }, idx) => {
        processedHtml = processedHtml
          .split(blobUrl)
          .join(`__PENDING_IMG_${idx}__`);
      });
      formDataObj.append("content", processedHtml);

      descImgFiles.forEach(({ file }) => {
        formDataObj.append("noticeImgs", file);
      });

      attachFiles.forEach((file) => {
        formDataObj.append("noticeFiles", file);
      });

      console.log("processedHtml:", processedHtml); // 여기
      console.log("descImgFiles:", descImgFiles); // 여기

      const response = await apiPostFormData("/admin/notice", formDataObj);

      if (response.ok) {
        const message = await response.text();
        alert(message || "공지사항이 등록되었습니다.");
        if (onAdd) onAdd();
        onClose();
      } else {
        alert("공지사항 등록에 실패했습니다.");
      }
    } catch (err) {
      console.error("등록 실패:", err);
      alert("공지사항 등록 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="adminnoticeadd-backdrop">
      <div className="adminnoticeadd-wrap">
        <div className="adminnoticeadd-header">
          <h2 className="adminnoticeadd-title">공지사항 작성</h2>
          <button className="adminnoticeadd-close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="adminnoticeadd-content">
          <div className="adminnoticeadd-form-group">
            <label className="adminnoticeadd-label">제목</label>
            <input
              type="text"
              className="adminnoticeadd-input"
              placeholder="제목을 입력하세요"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <label className="adminnoticeadd-checkbox-label">
              <input
                type="checkbox"
                className="adminnoticeadd-checkbox"
                checked={isPinned}
                onChange={(e) => setIsPinned(e.target.checked)}
              />
              <span>고정</span>
            </label>
          </div>

          <div className="adminnoticeadd-form-group">
            <label className="adminnoticeadd-label">공지 구분</label>
            <select
              className="adminnoticeadd-select"
              value={noticeType}
              onChange={(e) => setNoticeType(e.target.value)}
            >
              <option value="공통">전체</option>
              <option value="사용자">사용자</option>
              <option value="가맹점">가맹점</option>
            </select>
          </div>

          <div className="adminnoticeadd-form-group">
            <label className="adminnoticeadd-label">내용</label>
            <div className="desc-editor-wrap">
              <div className="desc-editor-toolbar">
                <button
                  type="button"
                  className="desc-insert-image-btn"
                  onClick={() => {
                    const sel = window.getSelection();
                    if (
                      sel.rangeCount > 0 &&
                      descEditorRef.current.contains(sel.anchorNode)
                    ) {
                      savedRangeRef.current = sel.getRangeAt(0).cloneRange();
                    }
                    descImgInputRef.current?.click();
                  }}
                >
                  📷 이미지 삽입
                </button>
                <input
                  ref={descImgInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png"
                  style={{ display: "none" }}
                  onChange={handleDescImageInsert}
                />
              </div>
              <div
                ref={descEditorRef}
                contentEditable={true}
                className="desc-editor-content"
                suppressContentEditableWarning={true}
                data-placeholder="내용을 입력하세요"
              />
            </div>
          </div>

          <div className="adminnoticeadd-form-group">
            <label className="adminnoticeadd-label">첨부파일</label>
            <label className="adminnoticeadd-file-btn">
              파일 선택
              <input
                type="file"
                multiple
                onChange={handleAttachFileChange}
                style={{ display: "none" }}
              />
            </label>
            {attachFiles.length > 0 && (
              <ul className="adminnoticeadd-file-list">
                {attachFiles.map((file, idx) => (
                  <li key={idx} className="adminnoticeadd-file-item">
                    <span className="adminnoticeadd-file-name">
                      {file.name}
                    </span>
                    <button
                      type="button"
                      className="adminnoticeadd-file-remove-btn"
                      onClick={() => handleAttachFileRemove(idx)}
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="adminnoticeadd-footer">
          <button
            className="adminnoticeadd-btn adminnoticeadd-btn-submit"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "저장 중..." : "저장"}
          </button>
          <button
            className="adminnoticeadd-btn adminnoticeadd-btn-cancel"
            onClick={onClose}
            disabled={loading}
          >
            취소
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminNoticeAdd;
